import React from 'react';
import { Box, Button, IconButton } from '@mui/material';
import { VolumeUp as SpeakerIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import UnitPageShell from '../../components/shared/UnitPageShell';
import ZoomableImage from '../../components/shared/ZoomableImage';
import EditableTextBlock from '../../components/shared/EditableTextBlock';
import { useAuth } from '../../context/AuthContext';
import { useTTS } from '../../hooks/useTTS';
import { uploadUnitAsset, updateUnitContent, useUnitOverride } from '../../services/unitOverridesService';

const PAGE_ID = 'm6_u4_x4_vsr';
const IMAGE_BASE = '/assets/drawable/';

type TextBlock = { heading: string; body: string };

const textBlocks: TextBlock[] = [
  { heading: 'Einführung', body: 'Volumenstromregler (VSR) halten einen eingestellten Luftvolumenstrom konstant, unabhängig von Druckschwankungen im Kanalnetz.' },
  { heading: 'Aufbau', body: '• Sensor/Regler-Einheit\n• Stellklappe mit Antrieb\n• Messstrecke für Differenzdruck' },
  { heading: 'Funktion', body: '• Regler vergleicht Ist- mit Soll-Volumenstrom\n• Stellt Klappenposition zur Einhaltung des Sollwerts nach\n• Dämpfung/Kennlinie parametrierbar' },
  { heading: 'Ansteuerung', body: '• Analog: 0–10V Sollwert\n• Bus: Kommunikationsschnittstellen je nach Gerät\n• Rückmeldung: Volumenstrom/Position' },
  { heading: 'Einstellung (Wertegrenzen)', body: '• Vmin und Vmax definieren Betriebsbereich\n• Skalierung und Messeinheit beachten\n• Einmessung nach Inbetriebnahme' },
  { heading: 'Regelkriterien', body: '• Störgrößen (Druckschwankungen)\n• Reglerparameter (Verstärkung/ Zeitkonstanten)\n• Stabilität und Genauigkeit' },
  { heading: 'Positionierung', body: '• Einbauort mit ausreichend geraden Kanalstrecken\n• Messöffnungen zugänglich halten\n• Wartung beachten' }
];

const images = [
  { id: 'vsr_aufbau', label: 'Volumenstromregler', src: `${IMAGE_BASE}vsr.jpg`, alt: 'Aufbau eines Volumenstromreglers' }
];

const Page: React.FC = () => {
  const navigate = useNavigate();
  const { token, canEditTextsM6, canUploadImagesM6 } = useAuth();
  const canEditTexts = canEditTextsM6;
  const canUploadImages = canUploadImagesM6;

  const { override, refresh } = useUnitOverride(PAGE_ID);

  const mergedBlocks = React.useMemo(
    () => textBlocks.map((tb, i) => ({ ...tb, body: override?.texts?.[`block_${i}`] || tb.body })),
    [override]
  );

  const imgSrc = (key: string, fallback: string) => {
    const v = override?.images?.[key];
    if (!v) return fallback;
    const s = String(v);
    return s.startsWith('/assets/') || s.startsWith('assets/') ? s : `/assets/unit_custom/${s}`;
  };

  const fullPlainText = React.useMemo(() => mergedBlocks.map(b => `${b.heading}. ${b.body}`).join(' '), [mergedBlocks]);
  const { speak, stop, speaking } = useTTS({ text: fullPlainText });

  const handleImageReplace = React.useCallback(async (imageKey: string, file: File) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await uploadUnitAsset(PAGE_ID, imageKey, file, token);
      alert('Bild erfolgreich aktualisiert.');
      await refresh();
    } catch (e: any) {
      alert('Upload fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const handleTextSave = React.useCallback(async (blockId: string, newBody: string) => {
    if (!token) return alert('Kein Token – bitte einloggen.');
    try {
      await updateUnitContent(PAGE_ID, { texts: { [blockId]: newBody } }, token);
      alert('Text gespeichert.');
      await refresh();
    } catch (e: any) {
      alert('Speichern fehlgeschlagen: ' + (e?.message || e));
    }
  }, [token, refresh]);

  const ttsButton = React.createElement(IconButton, { onClick: speaking ? stop : speak, 'aria-label': 'Text vorlesen', sx: { ml: 1 } }, React.createElement(SpeakerIcon, { sx: { fontSize: 28 } }));

  return React.createElement(UnitPageShell, { title: '6.4.4 Volumenstromregler (VSR)', actions: ttsButton },
    React.createElement(Box, { sx: { display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 } },
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        images.map(img => React.createElement(ZoomableImage, { key: img.id, src: imgSrc(img.id, img.src), alt: img.alt, label: img.label, onReplace: canUploadImages ? (file: File) => handleImageReplace(img.id, file) : undefined }))
      ),
      React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        mergedBlocks.map((block, i) => React.createElement(EditableTextBlock, { key: i, pageId: PAGE_ID, blockId: `block_${i}`, heading: block.heading, body: block.body, onSave: (newBody: string) => handleTextSave(`block_${i}`, newBody) })),
        React.createElement(Box, { sx: { display: 'flex', gap: 1, mt: 2 } }, React.createElement(Button, { variant: 'outlined', onClick: () => navigate('/units/m6_u4_x99_quiz') }, 'Zum Quiz'))
      )
    )
  );
};

export default Page;


